// Reason.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import { parseValidationErrors } from "../../../../apiHelper/validationHelper";
import Export from "../../../../formHelper/export";
import Search from "../../../../formHelper/search";
import Pagination from "../../../../formHelper/pagination";
import Table from "../../../../formHelper/list";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Success from "../../../../formHelper/success";
import Delete from "../../../../formHelper/delete";
import exportApiCall from "../../../../apiHelper/export";
import FilterDropdown from "../../../../formHelper/filterDropDown";
import Loader from "../../../../formHelper/Loader";
import dayjs from "dayjs";
import type { RootState, AppDispatch } from "../../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
interface ReasonType {
  id: number;
  name: string;
  order_type: string;
  created_at: string;
  branch?: { name?: string };
  [k: string]: any;
}

interface Branch {
  id: number;
  name: string;
}

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

const orderOptions = [
  { value: "pending_order", label: "Pending Order" },
  { value: "pending_shipment", label: "Pending Shipment" },
];

const Reason: React.FC = () => {
  const backendRoute = all_routes.backend;

  // list + form
  const [list, setList] = useState<ReasonType[]>([]);
  const [formData, setFormData] = useState<{
    name: string;
    order_type: string;
  }>({
    name: "",
    order_type: "",
  });
  const [formError, setFormError] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // filters / UI state
  const [branchFilter, setBranchFilter] = useState<number[]>([]);
  const [branchList, setBranchList] = useState<Branch[]>([]);
  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(() =>
    typeof window !== "undefined" && window.innerWidth <= 768 ? 5 : 10
  );
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  // flags
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // export
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // theme detection (keeps parity with Source)
  const [isDark, setIsDark] = useState<boolean>(() => {
    try {
      return (
        (window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches) ||
        document.body.classList.contains("dark-mode")
      );
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let mq: MediaQueryList | null = null;
    try {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
      if (mq?.addEventListener) mq.addEventListener("change", handler);
      else if (mq?.addListener) mq.addListener(handler);

      const obs = new MutationObserver(() =>
        setIsDark(document.body.classList.contains("dark-mode"))
      );
      obs.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => {
        if (mq) {
          if (mq.removeEventListener) mq.removeEventListener("change", handler);
          else if (mq.removeListener) mq.removeListener(handler);
        }
        obs.disconnect();
      };
    } catch {
      // noop
    }
  }, []);

  // inject minimal helper CSS (same technique used in Source)
  useEffect(() => {
    const styleId = "reason-responsive-styles";
    if (document.getElementById(styleId)) return;

    const css = `
      .page-header { margin-bottom: 0.5rem; }
      .count-title, .count-badge { background: rgba(0,0,0,0.04); padding:4px 8px; border-radius:8px; font-weight:600; }
      .icon-form .form-icon { margin-right:0.6rem; display:flex; align-items:center; position:absolute; left:0.85rem; top:50%; transform:translateY(-50%); pointer-events:none; }
      .card-control .form-control.bookingrange { padding-left: 2.6rem; }
      .filter-dropdown-menu .filter-set-view { min-width:220px; }
      .offcanvas.show { visibility: visible !important; }
    `;

    const node = document.createElement("style");
    node.id = styleId;
    node.appendChild(document.createTextNode(css));
    document.head.appendChild(node);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // refs + debounce (to mirror Source)
  const mountedRef = useRef(true);
  const paramsRef = useRef({
    page: currentPage,
    perPage,
    query,
    startDate,
    endDate,
    branchFilter,
  });
  const searchDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    paramsRef.current = {
      page: currentPage,
      perPage,
      query,
      startDate,
      endDate,
      branchFilter,
    };
  }, [currentPage, perPage, query, startDate, endDate, branchFilter]);

  useEffect(() => {
    mountedRef.current = true;
    // initial loads
    fetchList();
    getBranch();

    const onResize = () => {
      if (window.innerWidth <= 768) setPerPage(5);
      else if (perPage < 10) setPerPage(10);
    };
    window.addEventListener("resize", onResize);

    return () => {
      mountedRef.current = false;
      window.removeEventListener("resize", onResize);
      if (searchDebounceRef.current)
        window.clearTimeout(searchDebounceRef.current);
    };
  }, [branchFilter]);

  // debounce search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = window.setTimeout(() => {
      setCurrentPage(1);
      fetchList({ page: 1, perPage: paramsRef.current.perPage });
    }, 450);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    const isMobileWidth = () =>
      typeof window !== "undefined" && window.innerWidth <= 767.98;

    // initial
    if (isMobileWidth()) {
      setPerPage(5);
      setCurrentPage(1);
    } else {
      setPerPage((p) => (p ? p : 10));
    }

    const onResize = () => {
      const mobile = isMobileWidth();
      setPerPage((prev) => {
        const desired = mobile ? 5 : 10;
        if (prev !== desired) {
          setCurrentPage(1);
          return desired;
        }
        return prev;
      });
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // safe setter that verifies mounted
  const safeSet = useCallback(<T,>(setter: (v: T) => void, val: T) => {
    if (mountedRef.current) setter(val);
  }, []);

  // ---------- API helpers ----------
  const fetchList = useCallback(
    async (params?: { page?: number; perPage?: number }) => {
      const payloadPage = params?.page ?? paramsRef.current.page ?? 1;
      const payloadPer =
        params?.perPage ?? paramsRef.current.perPage ?? perPage;

      safeSet(setLoading, true);
      try {
        const result = await apiRequestCall("post", backendRoute.getReason, {
          data: {
            page: payloadPage,
            per_page: payloadPer,
            start_date: paramsRef.current.startDate,
            end_date: paramsRef.current.endDate,
            search: (paramsRef.current.query || "").trim(),
            branch_list: paramsRef.current.branchFilter,
          },
        });

        const res = result?.data?.data;
        if (res) {
          safeSet(setList, res.data || []);
          safeSet(setCurrentPage, res.current_page ?? payloadPage);
          safeSet(setPerPage, res.per_page ?? payloadPer);
          safeSet(setTotalItems, res.total ?? 0);
        } else {
          safeSet(setList, []);
        }
      } catch (err) {
        console.error("Failed to fetch reasons:", err);
        safeSet(setList, []);
      } finally {
        if (mountedRef.current) safeSet(setLoading, false);
      }
    },
    [backendRoute.getReason, perPage, safeSet]
  );

  const getBranch = useCallback(async () => {
    try {
      const result = await apiRequestCall("post", backendRoute.getBranchDD);
      const raw = result?.data?.data;
      if (raw && typeof raw === "object") {
        const arr = Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }));
        safeSet(setBranchList, arr);
      }
    } catch (err) {
      console.error("getBranch failed", err);
    }
  }, [backendRoute.getBranchDD, safeSet]);

  // ---------- export helpers ----------
  const triggerDownload = (url: string, filename: string, revoke = false) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloading(true);
    setTimeout(() => setDownloading(false), 3000);
    setExporting(false);
  };

  // Poll API for local download
  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, true);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  const exportFile = useCallback(async () => {
    try {
      safeSet(setExporting, true);
      safeSet(setDownloading, false);

      const result = await apiRequestCall("post", backendRoute.getReason, {
        data: {
          search: (query || "").trim(),
          start_date: startDate,
          end_date: endDate,
          branch_list: branchFilter,
          download: true,
        },
      });

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;

      if (!filename) {
        safeSet(setExporting, false);
        alert("Export request failed. Please try again.");
        return;
      }

      pollForFile(filename);
    } catch (error) {
      console.error("Export request failed:", error);
      safeSet(setExporting, false);
      safeSet(setDownloading, false);
      alert("Export failed. Please try again.");
    }
  }, [
    backendRoute.getReason,
    branchFilter,
    pollForFile,
    query,
    startDate,
    endDate,
    triggerDownload,
    safeSet,
  ]);

  // ---------- form / actions ----------
  const inputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormError((prev) => {
        const updated = { ...prev };
        delete (updated as any)[name];
        return updated;
      });
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormData({ name: "", order_type: "" });
    setFormError({});
    setSelectedId(null);
    setShowForm(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        reason_id: selectedId,
        update_status: !!selectedId,
      };

      const result = await apiRequestCall(
        "post",
        backendRoute.addUpdateReason,
        {
          data: payload,
        }
      );

      if (result?.data?.code === 221) {
        setFormError(parseValidationErrors(result?.data?.errors));
        return;
      }

      if (result?.data?.code === 200) {
        resetForm();
        setShowSuccess(true);
        setShowMessage(result?.data?.message || "");
        fetchList({ page: currentPage, perPage });
      }
    } catch (err) {
      console.error("Submit failed:", err);
      alert("Request failed. Please try again.");
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [
    backendRoute.addUpdateReason,
    currentPage,
    formData,
    perPage,
    resetForm,
    selectedId,
    submitting,
    fetchList,
  ]);

  const updateRecord = useCallback(
    (id: number) => {
      setFormError({});
      const selected = list.find((x) => x.id === id);
      if (!selected) return;
      setFormData({
        name: selected.name || "",
        order_type: selected.order_type || "",
      });
      setSelectedId(selected.id);
      setShowForm(true);
    },
    [list]
  );

  const deleteRecord = useCallback((id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteId) return;
    if (deleting) return;
    setDeleting(true);
    try {
      const result = await apiRequestCall("post", backendRoute.deleteReason, {
        data: { id: deleteId },
      });
      if (result?.data?.code === 200) {
        fetchList({ page: currentPage, perPage });
        setShowMessage(result?.data?.message || "");
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed. Please try again.");
    } finally {
      setDeleteId(null);
      setShowDeleteModal(false);
      if (mountedRef.current) setDeleting(false);
    }
  }, [
    backendRoute.deleteReason,
    currentPage,
    deleteId,
    perPage,
    deleting,
    fetchList,
  ]);

  // ---------- memoized UI pieces ----------
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  const exportOptions = useMemo(
    () => [
      {
        label: downloading
          ? "Downloading..."
          : exporting
          ? "Generating..."
          : "Export",
        iconClass: downloading
          ? "ti ti-download"
          : exporting
          ? "ti ti-loader"
          : "ti ti-file-type-xls",
        colorClass: exporting || downloading ? "text-muted" : "text-danger",
        onClick: exporting || downloading ? undefined : exportFile,
      },
    ],
    [downloading, exporting, exportFile]
  );

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  const columns = useMemo(
    () => [
      {
        key: "id",
        label: "Sr. No",
        render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
      },
      { key: "name", label: "Reason Name" },
      { key: "branch.name", label: "Branch" },
      {
        key: "created_at",
        label: "Date",
      },
      ...(permissionMap?.reason?.edit_reason ||
      permissionMap?.reason?.delete_reason
        ? [
            {
              key: "action",
              label: "Action",
              render: (row: ReasonType) => (
                <div className="hstack gap-2 fs-15">
                  {permissionMap?.reason?.edit_reason && (
                    <button
                      className="btn btn-icon btn-sm btn-soft-info"
                      onClick={() => updateRecord(row.id)}
                      disabled={submitting}
                      title="Edit"
                    >
                      <i className="feather-edit" />
                    </button>
                  )}

                  {permissionMap?.reason?.delete_reason && (
                    <button
                      className="btn btn-icon btn-sm btn-soft-danger"
                      onClick={() => deleteRecord(row.id)}
                      disabled={submitting}
                      title="Delete"
                    >
                      <i className="feather-trash" />
                    </button>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [currentPage, perPage, submitting, deleting, updateRecord, deleteRecord]
  );

  // auth
  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  // render
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header: Title | Export | New */}
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title">
                  Reason <span className="count-title">{totalItems}</span>
                </h4>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex justify-content-end gap-2">
                  {permissionMap?.["reason"]?.["export_reason"] && (
                    <Export options={exportOptions} />
                  )}

                  {permissionMap?.["reason"]?.["add_reason"] && (
                    <button
                      className="btn btn-soft-success d-flex align-items-center"
                      onClick={() => {
                        resetForm();
                        setShowForm(true);
                      }}
                      disabled={submitting}
                      aria-label="Add new"
                    >
                      <i className="ti ti-square-rounded-plus" />
                      <span className="d-none d-md-inline ms-1">New</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top card: search / date / filter */}
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <div className="icon-form position-relative">
                  <span className="form-icon">
                    <i className="ti ti-search" />
                  </span>
                  <Search
                    placeholder="Search Reason"
                    value={query}
                    onChange={setQuery}
                  />
                </div>
              </div>

              <div className="col-12 col-md-3">
                <div className="icon-form position-relative">
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>
                  <DateRangePicker
                    initialSettings={{
                      autoUpdateInput: false,
                      locale: { cancelLabel: "Clear" },
                    }}
                    onCallback={(start, end) => {
                      setStartDate(start.format("YYYY-MM-DD"));
                      setEndDate(end.format("YYYY-MM-DD"));
                      setCurrentPage(1);
                    }}
                    onCancel={() => {
                      setStartDate("");
                      setEndDate("");
                      setCurrentPage(1);
                    }}
                  >
                    <input
                      type="text"
                      className="form-control bookingrange"
                      placeholder="Select Date Range Filter"
                      value={
                        startDate && endDate
                          ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                              endDate
                            ).format("DD-MM-YYYY")}`
                          : ""
                      }
                      readOnly
                      style={{ cursor: "pointer" }}
                      aria-label="Select date range"
                    />
                  </DateRangePicker>
                </div>
              </div>

              <div className="col-12 col-md-3">
                <FilterDropdown
                  label="Branch"
                  endpoint="/getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              {loading ? <Loader /> : <Table data={list} columns={columns} />}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(p) => {
              setCurrentPage(p);
              fetchList({ page: p, perPage });
            }}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setCurrentPage(1);
              fetchList({ page: 1, perPage: pp });
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
            ]}
          />
        </div>

        {/* Offcanvas Form */}
        <div
          className={`offcanvas offcanvas-end offcanvas-large ${
            showForm ? "show" : ""
          }`}
          style={{ visibility: showForm ? "visible" : "hidden" }}
          tabIndex={-1}
          aria-modal={showForm}
          role="dialog"
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">
              {selectedId ? "Update Reason" : "Add Reason"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowForm(false)}
              disabled={submitting}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="offcanvas-body">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!submitting) handleSubmit();
              }}
            >
              <div className="mb-3">
                <label className="col-form-label">
                  Reason Name <span className="text-danger">*</span>
                </label>
                <input
                  name="name"
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.name && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.name}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Select Reason <span className="text-danger">*</span>
                </label>
                <Select
                  placeholder="Select Order Type"
                  name="order_type"
                  classNamePrefix="react-select"
                  className="tagging"
                  options={orderOptions}
                  value={
                    orderOptions.find(
                      (opt) => opt.value === formData.order_type
                    ) || null
                  }
                  onChange={(selected: any) => {
                    setFormError((prev) => {
                      const updated = { ...prev };
                      delete (updated as any).order_type;
                      return updated;
                    });
                    setFormData((prev) => ({
                      ...prev,
                      order_type: selected?.value || "",
                    }));
                  }}
                  isClearable
                  isDisabled={submitting}
                />
                {formError.order_type && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.order_type}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-soft-primary me-2"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-soft-success"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {selectedId ? "Updating..." : "Creating..."}
                    </>
                  ) : selectedId ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <Success
          id="ReasonSuccessModal"
          title="Reason"
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />

        <Delete
          show={showDeleteModal}
          title="Delete Reason?"
          message="Are you sure?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default Reason;
